import _slicedToArray from "/Users/user/code/other/form-ratings/example/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _toArray from "/Users/user/code/other/form-ratings/example/node_modules/@babel/runtime/helpers/esm/toArray";
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'formik';
var COLOR_RATED = '#f7b500';
var COLOR_HOVER = COLOR_RATED;
var DEFAULT_SCALE = 24; // http://mathworld.wolfram.com/Pentagram.html for the
// geometry of a 5-pointed star

var DIST_A = 1 / (2 + 2 * Math.sin(Math.PI / 10));
var DIST_B = DIST_A * 2 * Math.sin(Math.PI / 10);
var DIST_P = DIST_A * Math.cos(Math.PI / 10);
var POINT_RADIUS = DIST_A / 10;
var LINE_LENGTH_DIFF = POINT_RADIUS / Math.tan(Math.PI / 10); // Point positions of the star
// Each point is a pair where the lines stop and an arc is formed

var POINT_A = [DIST_A + (DIST_A - LINE_LENGTH_DIFF) * Math.cos(2 * Math.PI / 5), LINE_LENGTH_DIFF * Math.sin(2 * Math.PI / 5)];
var POINT_A2 = [DIST_A + DIST_B - (DIST_A - LINE_LENGTH_DIFF) * Math.cos(2 * Math.PI / 5), LINE_LENGTH_DIFF * Math.sin(2 * Math.PI / 5)];
var POINT_B = [DIST_A * 2 + DIST_B - LINE_LENGTH_DIFF, DIST_P];
var POINT_B2 = [DIST_A * 2 + DIST_B - LINE_LENGTH_DIFF * Math.cos(Math.PI / 5), DIST_P + LINE_LENGTH_DIFF * Math.sin(Math.PI / 5)];
var POINT_C = [DIST_A + DIST_B + (DIST_B + DIST_A - LINE_LENGTH_DIFF) * Math.cos(2 * Math.PI / 5), DIST_P + (DIST_B + DIST_A - LINE_LENGTH_DIFF) * Math.sin(2 * Math.PI / 5)];
var POINT_C2 = [DIST_A + DIST_B + (DIST_B + DIST_A) * Math.cos(2 * Math.PI / 5) - LINE_LENGTH_DIFF * Math.cos(Math.PI / 5), DIST_P + (DIST_B + DIST_A) * Math.sin(2 * Math.PI / 5) - LINE_LENGTH_DIFF * Math.sin(Math.PI / 5)];
var POINT_D = [DIST_A - (DIST_B + DIST_A) * Math.cos(2 * Math.PI / 5) + LINE_LENGTH_DIFF * Math.cos(Math.PI / 5), DIST_P + (DIST_B + DIST_A) * Math.sin(2 * Math.PI / 5) - LINE_LENGTH_DIFF * Math.sin(Math.PI / 5)];
var POINT_D2 = [DIST_A - (DIST_B + DIST_A - LINE_LENGTH_DIFF) * Math.cos(2 * Math.PI / 5), DIST_P + (DIST_B + DIST_A - LINE_LENGTH_DIFF) * Math.sin(2 * Math.PI / 5)];
var POINT_E = [LINE_LENGTH_DIFF * Math.cos(Math.PI / 5), DIST_P + LINE_LENGTH_DIFF * Math.sin(Math.PI / 5)];
var POINT_E2 = [LINE_LENGTH_DIFF, DIST_P]; // polygon positions of the pentagon

var POLY_A = [DIST_A, DIST_P];
var POLY_B = [DIST_A + DIST_B, DIST_P];
var POLY_C = [DIST_A + DIST_B * (1 + Math.cos(2 * Math.PI / 5)), DIST_P + DIST_B * Math.sin(2 * Math.PI / 5)];
var POLY_D = [DIST_A + DIST_B / 2, DIST_P * 2];
var POLY_E = [DIST_A - DIST_B * Math.cos(2 * Math.PI / 5), DIST_P + DIST_B * Math.sin(2 * Math.PI / 5)];
var fracWhole = [['M', POINT_A], ['A', [POINT_RADIUS, POINT_RADIUS], '0,0,1', POINT_A2], ['L', POLY_B], ['L', POINT_B], ['A', [POINT_RADIUS, POINT_RADIUS], '0,0,1', POINT_B2], ['L', POLY_C], ['L', POINT_C], ['A', [POINT_RADIUS, POINT_RADIUS], '0,0,1', POINT_C2], ['L', POLY_D], ['L', POINT_D], ['A', [POINT_RADIUS, POINT_RADIUS], '0,0,1', POINT_D2], ['L', POLY_E], ['L', POINT_E], ['A', [POINT_RADIUS, POINT_RADIUS], '0,0,1', POINT_E2], ['L', POLY_A], ['L', POINT_A]];

var generateStarFraction = function generateStarFraction(fraction) {
  return [{
    key: 'outline',
    path: [['M', [0, 0]], ['L', [1, 0]], ['L', [1, 1]], ['L', [0, 1]], ['z']].concat(fracWhole, [['z']])
  }, {
    key: 'mask',
    path: [['M', [fraction, 0]], ['L', [1, 0]], ['L', [1, 1]], ['L', [fraction, 1]]]
  }];
};

var drawPath = function drawPath(scale, path) {
  return path.map(function (_ref) {
    var _ref2 = _toArray(_ref),
        op = _ref2[0],
        points = _ref2.slice(1);

    return "".concat(op, " ").concat(points.map(function (value) {
      if (Array.isArray(value)) {
        return value.map(function (pix) {
          return pix * scale;
        }).join(',');
      }

      return value;
    }).join(' '));
  }).join(' ');
};

var Star = function Star(_ref3) {
  var fraction = _ref3.fraction,
      _ref3$scale = _ref3.scale,
      scale = _ref3$scale === void 0 ? DEFAULT_SCALE : _ref3$scale,
      _ref3$color = _ref3.color,
      color = _ref3$color === void 0 ? COLOR_RATED : _ref3$color;
  return React.createElement("svg", {
    width: scale,
    height: scale
  }, fraction === 1 && React.createElement("path", {
    d: drawPath(scale, fracWhole),
    stroke: color,
    strokeWidth: 1,
    fill: color
  }), fraction > 0 && fraction < 1 && React.createElement(React.Fragment, null, React.createElement("path", {
    d: drawPath(scale, fracWhole),
    stroke: "none",
    fill: color
  }), generateStarFraction(fraction).map(function (_ref4) {
    var path = _ref4.path,
        key = _ref4.key;
    return React.createElement("path", {
      key: key,
      d: drawPath(scale, path),
      fillRule: "evenodd",
      fill: "white"
    });
  })), fraction < 1 && React.createElement("path", {
    d: drawPath(scale, fracWhole),
    stroke: color,
    strokeWidth: 1,
    fill: "none"
  }));
};

var StarContainer = function StarContainer(_ref5) {
  var fraction = _ref5.fraction,
      onHover = _ref5.onHover,
      onChange = _ref5.onChange,
      _ref5$hoverRating = _ref5.hoverRating,
      hoverRating = _ref5$hoverRating === void 0 ? 0 : _ref5$hoverRating,
      color = _ref5.color;
  var onMouseOver = useCallback(function () {
    return onHover && onHover(hoverRating);
  }, [onHover, hoverRating]);
  var onClick = useCallback(function () {
    return onChange && onChange(hoverRating);
  }, [onChange, hoverRating]);
  var onKeyDown = useCallback(function (event) {
    if (!onChange) {
      return;
    }

    if (event.key === 'Enter') {
      onChange(hoverRating);
    }
  }, [onChange, hoverRating]);
  return React.createElement("div", {
    role: "button",
    tabIndex: 0,
    className: "star-container",
    onMouseOver: onMouseOver,
    onFocus: onMouseOver,
    onClick: onClick,
    onKeyDown: onKeyDown
  }, React.createElement(Star, {
    fraction: fraction,
    color: color
  }));
};

var flex = {
  display: 'inline-flex'
};

var Stars = function Stars(_ref6) {
  var value = _ref6.value,
      color = _ref6.color,
      onHover = _ref6.onHover,
      onChange = _ref6.onChange;
  var stars = new Array(5).fill(0).map(function (item, index) {
    return {
      key: "star-".concat(index),
      fraction: value > index + 1 ? 1 : Math.max(0, value - index)
    };
  });
  return React.createElement("div", {
    style: flex
  }, stars.map(function (_ref7, index) {
    var key = _ref7.key,
        fraction = _ref7.fraction;
    return React.createElement(StarContainer, {
      key: key,
      fraction: fraction,
      onHover: onHover,
      onChange: onChange,
      hoverRating: index + 1,
      color: color
    });
  }));
};

var RatingsField = function RatingsField(_ref8) {
  var name = _ref8.name,
      value = _ref8.value,
      setFieldValue = _ref8.formik.setFieldValue;
  var numberValue = Number(value);
  var onChange = useCallback(function (newValue) {
    return setFieldValue(name, newValue);
  }, [name, setFieldValue]);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      hoverRating = _useState2[0],
      setHoverRating = _useState2[1];

  var onReset = useCallback(function () {
    return setHoverRating(null);
  }, []);
  var rating = hoverRating || numberValue;
  var color = hoverRating ? COLOR_HOVER : COLOR_RATED;
  return React.createElement("div", {
    className: "form-ratings",
    onMouseLeave: onReset,
    onBlur: onReset
  }, React.createElement(Stars, {
    value: rating,
    onHover: setHoverRating,
    onChange: onChange,
    color: color
  }));
};

RatingsField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.number,
  formik: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    setStatus: PropTypes.func.isRequired,
    setErrors: PropTypes.func.isRequired,
    setSubmitting: PropTypes.func.isRequired,
    setTouched: PropTypes.func.isRequired,
    setValues: PropTypes.func.isRequired,
    setFieldError: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    validateForm: PropTypes.func.isRequired,
    validateField: PropTypes.func.isRequired,
    submitForm: PropTypes.func.isRequired,
    resetForm: PropTypes.func.isRequired,
    setFormikState: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleReset: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    getFieldProps: PropTypes.func.isRequired,
    getFieldMeta: PropTypes.func.isRequired,
    registerField: PropTypes.func.isRequired,
    unregisterField: PropTypes.func.isRequired,
    dirty: PropTypes.bool.isRequired,
    isValid: PropTypes.bool.isRequired,
    values: PropTypes.objectOf(PropTypes.shape({}).isRequired).isRequired,
    initialValues: PropTypes.objectOf(PropTypes.shape({}).isRequired).isRequired,
    errors: PropTypes.object.isRequired,
    initialErrors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    initialTouched: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    isValidating: PropTypes.bool.isRequired,
    submitCount: PropTypes.number.isRequired
  }).isRequired
};
RatingsField.defaultProps = {
  value: 0
};
var index = connect(RatingsField);
export default index;
export { Stars };